import React from 'react';
import classnames from 'classnames';
import { RuleLineText, LineTextOption, Font } from '@/sunzi';
import { Icon } from '@/components';
import { i18n } from '@/utils';
import styles from './style.less';


export interface EditTextProps {
  active: LineTextOption;
  rule?: RuleLineText;
  onApply: (lineText: LineTextOption) => void
}

export interface EditTextState {
  error: boolean;
  current: LineTextOption;
}

class EditText extends React.Component<EditTextProps, EditTextState> {

  constructor(props: EditTextProps) {
    super(props);
    // 避免警告 A component is changing an uncontrolled input of type text to be controlled
    props.active._value = props.active._value || '';
    this.state = {
      current: props.active,
      error: this._validator(props.active._value).error,
    }
  }

  /**
   * 刻字规则校验
   */
  private _validator = (value: string) : {
    error: boolean;
    message: string;
  } => {
    const { rule } = this.props;
    let error = false;
    let message: string = '';
    // 校验必填项
    if (rule) {
      if (rule.isRequire && value.length === 0) {
        error = true;
        message = '必填项校验失败';
      } else if (rule.minLength && value.length < rule.minLength) {
        error = true;
        message = '最小长度校验失败';
      } else if (rule.maxLength && value.length > rule.maxLength) {
        error = true;
        message = '最大长度校验失败';
      }
    }
    return {
      error,
      message
    };
  }

  /** 文字 onChange */
  handleValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    this.setState({
      current: {
        ...this.state.current,
        _value: value,
        value,
      },
      error: this._validator(value).error
    });
  }

  /** 字号 onChange */
  handleFontSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      current: {
        ...this.state.current,
        fontSize: parseInt(e.target.value)
      }
    });
  }

  /** 字体 onChange */
  handleFontChange = (font: Font) => () => {
    this.setState({
      current: {
        ...this.state.current,
        font
      }
    });
  }

  /** 字体颜色 onChange */
  handleFontColorChange = (fontColor: string) => () => {
    this.setState({
      current: {
        ...this.state.current,
        fontColor
      }
    });
  }

  /** 字体阴影颜色 onChange */
  handleFontShadowColorChange = (fontShadowColor: string) => () => {
    this.setState({
      current: {
        ...this.state.current,
        fontShadowColor
      }
    });
  }

  /** 提交 */
  handleApply = async () => {
    const { error, current } = this.state;
    if (error) return;
    this.props.onApply(current);
  }

  render() {
    const { error, current } = this.state;
    const { rule } = this.props;

    const countStyle = classnames(
      styles.currentCount, {
        [styles.currentCountError]: error
      }
    );

    const applyStyle = classnames(
      styles.applyButton, {
        [styles.applyButtonDisabled]: error
      }
    );
      
    return (
      <div className={styles.edit}>
        <div className={styles.editContent}>
          <div className={styles.title}>{i18n.format('components.edit.title')}</div>
          {/* 文字 */}
          <div className={styles.block}>
            <div className={styles.blockTitle}>{i18n.format('components.edit.font.text')}</div>
            <div className={styles.blockInputWrap}>
              {rule && rule.supportWrap ? (
                <textarea value={current._value} onChange={this.handleValueChange}/>
              ) : <input value={current._value} type="text" onChange={this.handleValueChange}/> }
              <div className={styles.inputValidatorCount}>
                <span className={countStyle}>{(current._value || '').length}</span>
                {rule && rule.maxLength && 
                  <span>/{rule.maxLength}</span>
                }
              </div>
            </div>
          </div>
          {/* 字体 */}
          {rule && rule.fonts.length > 1 && (
            <div className={styles.block}>
              <div className={styles.blockTitle}>{i18n.format('components.edit.font.family')}</div>
              <div className={styles.blockContent}>
                {rule.fonts.map(item => {             
                  const fontStyle = classnames(
                    styles.font, {
                      [styles.blockContentItemActive]: current.font.id === item.id
                    }
                  );
                  return (
                    <div key={item.id} className={styles.blockContentAdaptionItem} onClick={this.handleFontChange(item)}>
                      <div className={fontStyle} style={{ fontFamily: `${item.name}-subset` }}>{item.name}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* 字号 */}
          {rule && rule.fontSizes.length > 1 && (
            <div className={styles.block}>
              <div className={styles.blockTitle}>{i18n.format('components.edit.font.size')}</div>
              <div className={styles.blockSelectWrap}>
                <div className={styles.blockSelect}>
                  <div className={styles.blockSelectValue}>{current.fontSize}</div>
                  <Icon type="down" color="#a1a1a1" />
                </div>
                <select value={current.fontSize} onChange={this.handleFontSizeChange}>
                  {rule.fontSizes.map(item => 
                    <option key={item} value={item}>{item}</option>
                  )}
                </select>
              </div>
            </div>
          )}
          {/* 字体颜色  */}
          {rule && rule.fontColors.length > 1 && (
            <div className={styles.block}>
              <div className={styles.blockTitle}>{i18n.format('components.edit.font.color')}</div>
              <div className={styles.blockContent}>
                {rule.fontColors.map((item, index) => {      
                  const colorStyle = classnames(
                    styles.color, {
                      [styles.blockContentItemActive]: current.fontColor === item
                    }
                  );
                  return (
                    <div key={index} className={styles.blockContentAdaptionItem} onClick={this.handleFontColorChange(item)}>
                      <div className={colorStyle}>
                        <div style={{ background: item }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className={styles.editFooter}>
          <div className={applyStyle} onClick={this.handleApply}>{i18n.format('components.edit.apply')}</div>
        </div>
      </div>
    )
  }
};

export default EditText;